import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { ConsolGroupEditService } from './consol-group-edit.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-consol-group-edit',
    templateUrl: './consol-group-edit.component.html',
    styleUrls: ['./consol-group-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ConsolGroupEditComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;
  private unsubscribe$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  loading = true;

  // Modal mode - empty until colData is received
  colDataMap = ''; // ADD_GROUP, GROUP_EDIT, MEMBERS_EDIT, ALLOCATE_LIABILITY, US_TAX_OWNER

  // Form visibility flags - default to false until init determines the mode
  showConsolGroupForm = false;
  showTaxOwnerForm = false;
  disabled = false;

  // Dropdown data
  tax_year_list: any[] = [];
  gold_head_leid_list: any[] = [];
  currency_list: any[] = [];
  taxOwnersList: any[] = [];
  countryList: any[] = [];
  nonMembersList: any[] = [];
  membersList: any[] = [];
  membersAmountList: any[] = [];

  // Selected values
  selectedFiscalStart: any = null;
  selectedFiscalEnd: any = null;
  selectedHeadLeid: any = null;
  selectedCurrency: any = null;
  selectedTaxOwner: any = null;
  selectedTaxOwnerStart: any = null;
  selectedTaxOwnerEnd: any = null;
  selectedGroupCountry: any = null;

  // Form data
  GROUP_TYPE = 'CH';
  GROUP_NAME = '';
  GROUP_CURRENCY = '';
  GROUP_ID = 0;

  // Member management
  memberDeleteList: any[] = [];
  nonMemerLoading = false;
  
  // Flag to prevent rowData setter from reverting selectedTaxOwner after save
  private justSavedTaxOwner = false;

  // Allocation data
  credit_us_amt: { [key: string]: number } = {};
  non_credit_us_amt: { [key: string]: number } = {};
  credit_us_amt_total = 0;
  non_credit_us_amt_total = 0;
  dateFiled: Date | null = null;

  // Calendar options
  calendar = {
    opened: false
  };

  // Screen filter data
  filter_tax_year = 0;
  raf_scenario = '';

  // Base configuration
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  colData: any = {};
  filterParams: any = {};

  @Input('col-data')
  set _colData(colData: string) {
    console.log('[ConsolGroupEdit] col-data raw input:', colData);
    
    // Handle empty or invalid col-data - default to ADD_GROUP mode
    if (!colData || colData.trim() === '' || colData.startsWith('{{')) {
      console.log('[ConsolGroupEdit] col-data is empty or unresolved, defaulting to ADD_GROUP');
      this.colData = { map: 'ADD_GROUP' };
      this.colDataMap = 'ADD_GROUP';
      this.tryInit();
      return;
    }
    
    try {
      const oldColDataMap = this.colDataMap;
      this.colData = safeJsonInput<any>(colData, null, 'consol-group-edit.parse1');
      // Set colDataMap from colData.map, default to 'ADD_GROUP' if not specified
      this.colDataMap = (this.colData && this.colData.map) ? this.colData.map : 'ADD_GROUP';
      console.log('[ConsolGroupEdit] colData parsed:', this.colData, 'colDataMap:', this.colDataMap);
      
      // If colDataMap changed to GROUP_EDIT after initialization, update form with rowData
      if (this.isInitialized && oldColDataMap !== this.colDataMap) {
        console.log('[ConsolGroupEdit] colDataMap changed from', oldColDataMap, 'to', this.colDataMap);
        
        // Update form visibility
        this.disabled = (this.colDataMap === 'GROUP_EDIT');
        this.showConsolGroupForm = (this.colDataMap === 'GROUP_EDIT' || this.colDataMap === 'ADD_GROUP');
        this.showTaxOwnerForm = (this.colDataMap === 'US_TAX_OWNER');
        
        // If switching to GROUP_EDIT and we have rowData, reinitialize form fields
        if (this.colDataMap === 'GROUP_EDIT' && this.rowData) {
          console.log('[ConsolGroupEdit] Reinitializing form fields for GROUP_EDIT mode');
          this.GROUP_TYPE = this.rowData.GROUP_TYPE || 'CH';
          this.GROUP_NAME = this.rowData.GROUP_NAME || '';
          this.GROUP_CURRENCY = this.rowData.CURRENCY || '';
          this.GROUP_ID = this.rowData.GROUP_ID || 0;
          
          // Update fiscal years if tax_year_list is loaded
          if (this.tax_year_list && this.tax_year_list.length > 0) {
            if (this.rowData.FISCAL_START) {
              const startInd = this.tax_year_list.findIndex((item: any) => item.TAX_YEAR === this.rowData.FISCAL_START);
              if (startInd >= 0) this.selectedFiscalStart = this.tax_year_list[startInd];
            }
            if (this.rowData.FISCAL_END) {
              const endInd = this.tax_year_list.findIndex((item: any) => item.TAX_YEAR === this.rowData.FISCAL_END);
              if (endInd >= 0) this.selectedFiscalEnd = this.tax_year_list[endInd];
            }
          }
          
          // Update currency if loaded
          if (this.currency_list && this.currency_list.length > 0 && this.rowData.CURRENCY) {
            const currInd = this.currency_list.findIndex((item: any) => item.CURR_CODE === this.rowData.CURRENCY);
            if (currInd >= 0) this.selectedCurrency = this.currency_list[currInd];
          }
          
          // Update tax owner if loaded
          if (this.taxOwnersList && this.taxOwnersList.length > 0 && this.rowData.GROUP_OWNER) {
            const ownerInd = this.taxOwnersList.findIndex((item: any) => item.SSO_ID === this.rowData.GROUP_OWNER);
            if (ownerInd >= 0) this.selectedTaxOwner = this.taxOwnersList[ownerInd];
          }
          
          console.log('[ConsolGroupEdit] Form fields reinitialized:', {
            GROUP_NAME: this.GROUP_NAME,
            GROUP_TYPE: this.GROUP_TYPE,
            GROUP_CURRENCY: this.GROUP_CURRENCY,
            selectedFiscalStart: this.selectedFiscalStart?.TAX_YEAR,
            selectedFiscalEnd: this.selectedFiscalEnd?.TAX_YEAR
          });
        }
        
        this.cdr.detectChanges();
      }
      
      this.tryInit();
    } catch (e) {
      console.log("[ConsolGroupEdit] col-data parse error, defaulting to ADD_GROUP:", e);
      this.colData = { map: 'ADD_GROUP' };
      this.colDataMap = 'ADD_GROUP';
      this.tryInit();
    }
  }

  constructor(
    private consolGroupEditService: ConsolGroupEditService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => { this.resetAndTryInit(); })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.resetAndTryInit(); })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) { fp = extractObject(hydrated); }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell, globalParams: mergedGp, filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.justSavedTaxOwner = false;
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  // V9: Gate function to prevent duplicate initialization
  private tryInit(): void {
    console.log('[ConsolGroupEdit] tryInit called', {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      hasGlobalService: !!this.globalService,
      hasGeneralConfig: !!this.GENERAL_CONFIG,
      hasBaseUrl: !!this.GENERAL_CONFIG?.base_url,
      hasColData: !!this.colData,
      colDataMap: this.colDataMap,
      rowData: this.rowData
    });
    
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Wait for all required data including colData
    if (!this.globalService || !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.log('[ConsolGroupEdit] Missing required data, waiting...');
      return;
    }
    
    // Wait for colData and colDataMap to be properly set
    if (!this.colData || !this.colDataMap) {
      console.log('[ConsolGroupEdit] Missing colData or colDataMap, waiting...', {
        hasColData: !!this.colData,
        colDataMap: this.colDataMap
      });
      return;
    }
    
    // Wait for rowData with GROUP_ID to be set (needed for save operations)
    // For ADD_GROUP mode, GROUP_ID is not required since we're creating a new group
    if (this.colDataMap !== 'ADD_GROUP') {
      if (!this.rowData || this.rowData.GROUP_ID === undefined) {
        console.log('[ConsolGroupEdit] Missing rowData or GROUP_ID, waiting...', {
          hasRowData: !!this.rowData,
          GROUP_ID: this.rowData?.GROUP_ID
        });
        return;
      }
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      console.log('[ConsolGroupEdit] init() started', {
        colData: this.colData,
        colDataMap: this.colDataMap,
        rowData: this.rowData,
        globalService: this.globalService,
        GENERAL_CONFIG: this.GENERAL_CONFIG
      });
      
      // colDataMap is already set by the input setter, just log it
      console.log('[ConsolGroupEdit] Using colDataMap:', this.colDataMap);
      
      // Set form visibility flags based on mode
      this.disabled = (this.colDataMap === 'GROUP_EDIT');
      this.showConsolGroupForm = (this.colDataMap === 'GROUP_EDIT' || this.colDataMap === 'ADD_GROUP');
      this.showTaxOwnerForm = (this.colDataMap === 'US_TAX_OWNER');
      
      console.log('[ConsolGroupEdit] Form visibility:', {
        showConsolGroupForm: this.showConsolGroupForm,
        showTaxOwnerForm: this.showTaxOwnerForm
      });

      // Initialize form data from rowData (matching AngularJS behavior)
      this.GROUP_TYPE = this.rowData ? (this.rowData.GROUP_TYPE || 'CH') : 'CH';
      this.GROUP_NAME = this.rowData ? (this.rowData.GROUP_NAME || '') : '';
      this.GROUP_CURRENCY = this.rowData ? (this.rowData.CURRENCY || '') : '';
      this.GROUP_ID = this.rowData ? (this.rowData.GROUP_ID || 0) : 0;
      
      console.log('[ConsolGroupEdit] Form data initialized:', {
        GROUP_TYPE: this.GROUP_TYPE,
        GROUP_NAME: this.GROUP_NAME,
        GROUP_CURRENCY: this.GROUP_CURRENCY,
        GROUP_ID: this.GROUP_ID,
        'rowData.GROUP_ID': this.rowData?.GROUP_ID,
        'rowData.GROUP_NAME': this.rowData?.GROUP_NAME,
        'rowData exists': !!this.rowData
      });
      
      // DEBUG: Show what GROUP_NAME value is from rowData vs what we set
      console.log('[ConsolGroupEdit] DEBUG - GROUP_NAME from rowData:', this.rowData?.GROUP_NAME, 
                  '| type:', typeof this.rowData?.GROUP_NAME,
                  '| final value set:', this.GROUP_NAME);

      // Reset amounts
      this.credit_us_amt_total = 0;
      this.non_credit_us_amt_total = 0;
      this.nonMemerLoading = false;

      // Get filter tax year
      this.filter_tax_year = parseInt(this.filterParams.tax_year) || parseInt(this.globalService?.globalParams?.tax_year) || 0;

      // Load RAF Scenario first
      this.getRAFScenario();

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  get modalTitle(): string {
    switch (this.colDataMap) {
      case 'ADD_GROUP':
        return 'Add Consolidated Group for TLC';
      case 'GROUP_EDIT':
        return 'Edit Consolidated Group for TLC';
      case 'MEMBERS_EDIT':
        return 'Manage Consolidated Group Members';
      case 'ALLOCATE_LIABILITY':
        return 'Consolidated Group Liability Allocation';
      case 'US_TAX_OWNER':
        return 'US Tax Owner';
      default:
        return 'Consolidated Group';
    }
  }

  getRAFScenario(): void {
    console.log('[ConsolGroupEdit] getRAFScenario called with tax_year:', this.filter_tax_year);
    
    const params = {
      tax_year: this.filter_tax_year
    };

    this.consolGroupEditService.getRAFScenario(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        console.log('[ConsolGroupEdit] getRAFScenario response:', data);
        
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading RAF scenario', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          // Still load mode specific data even on error
          this.loadModeSpecificData();
        } else {
          if (data.jsonObject && data.jsonObject.length > 0) {
            this.raf_scenario = data.jsonObject[0].SCENARIO;
          }
          this.loadModeSpecificData();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading RAF scenario:', error);
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadModeSpecificData(): void {
    console.log('[ConsolGroupEdit] Loading mode specific data for:', this.colDataMap);
    
    let dataLoaded = false;
    
    if (this.colDataMap === 'GROUP_EDIT' || this.colDataMap === 'ADD_GROUP') {
      dataLoaded = true;
      this.loadTaxYears();
      this.loadGoldHeadLeid(this.filter_tax_year);
      this.loadCurrencies();
      this.loadTaxOwners();
    }

    if (this.colDataMap === 'MEMBERS_EDIT') {
      dataLoaded = true;
      this.loadMembersList();
      this.loadCountryList();
    }

    if (this.colDataMap === 'ALLOCATE_LIABILITY') {
      dataLoaded = true;
      this.loadMembersAmtList();
    }

    if (this.colDataMap === 'US_TAX_OWNER') {
      dataLoaded = true;
      this.loadTaxOwners();
    }
    
    // If no mode matched, turn off loading anyway
    if (!dataLoaded) {
      console.warn('[ConsolGroupEdit] Unknown colDataMap:', this.colDataMap);
      this.showFormLoader = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  loadTaxYears(): void {
    console.log('[ConsolGroupEdit] loadTaxYears called');
    
    const params = {};

    this.consolGroupEditService.getTaxYears(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        console.log('[ConsolGroupEdit] loadTaxYears response:', data);
        
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading tax years', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.tax_year_list = data.jsonObject || [];
          console.log('[ConsolGroupEdit] tax_year_list set:', this.tax_year_list.length, 'items');

          if (this.colDataMap === 'GROUP_EDIT' && this.rowData) {
            // Find and set fiscal start
            const taxYearStartInd = this.tax_year_list.findIndex(
              (item: any) => item.TAX_YEAR === this.rowData.FISCAL_START
            );
            this.selectedFiscalStart = taxYearStartInd >= 0 ? this.tax_year_list[taxYearStartInd] : null;

            // Find and set fiscal end
            const taxYearEndInd = this.tax_year_list.findIndex(
              (item: any) => item.TAX_YEAR === this.rowData.FISCAL_END
            );
            this.selectedFiscalEnd = taxYearEndInd >= 0 ? this.tax_year_list[taxYearEndInd] : null;

            // Find and set tax owner start
            const taxOwnerStartInd = this.tax_year_list.findIndex(
              (item: any) => item.TAX_YEAR === this.rowData.TAX_OWNER_START
            );
            this.selectedTaxOwnerStart = taxOwnerStartInd >= 0 ? this.tax_year_list[taxOwnerStartInd] : null;

            // Find and set tax owner end
            const taxOwnerEndInd = this.tax_year_list.findIndex(
              (item: any) => item.TAX_YEAR === this.rowData.TAX_OWNER_END
            );
            this.selectedTaxOwnerEnd = taxOwnerEndInd >= 0 ? this.tax_year_list[taxOwnerEndInd] : null;
          } else {
            // Default to current filter tax year for ADD mode
            const currentYearInd = this.tax_year_list.findIndex(
              (item: any) => item.TAX_YEAR === this.filter_tax_year
            );
            const defaultYear = currentYearInd >= 0 ? this.tax_year_list[currentYearInd] : null;
            this.selectedFiscalStart = defaultYear;
            this.selectedFiscalEnd = defaultYear;
            this.selectedTaxOwnerStart = defaultYear;
            this.selectedTaxOwnerEnd = defaultYear;
          }
        }
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading tax years:', error);
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadGoldHeadLeid(tax_year_val: number): void {
    const params = {
      tax_year: tax_year_val,
      scenario: this.raf_scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key || 250
    };

    this.consolGroupEditService.getHeadleid(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading head LEID list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.gold_head_leid_list = data.jsonObject || [];

          if (this.colDataMap === 'GROUP_EDIT' && this.rowData) {
            const goldheadLeidInd = this.gold_head_leid_list.findIndex(
              (item: any) => item.LEID === this.rowData.GROUP_LEID
            );
            this.selectedHeadLeid = goldheadLeidInd >= 0 ? [{ LEID: this.rowData.GROUP_OWNER }] : null;
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading head LEID list:', error);
        this.cdr.detectChanges();
      }
    );
  }

  loadCurrencies(): void {
    const params = {
      tax_year: this.filter_tax_year
    };

    this.consolGroupEditService.getCurrencies(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading currencies', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.currency_list = data.jsonObject || [];

          if (this.colDataMap === 'GROUP_EDIT' && this.rowData) {
            const currencyInd = this.currency_list.findIndex(
              (item: any) => item.CURR_CODE === this.rowData.CURRENCY
            );
            this.selectedCurrency = currencyInd >= 0 ? this.currency_list[currencyInd] : null;
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading currencies:', error);
        this.cdr.detectChanges();
      }
    );
  }

  loadTaxOwners(): void {
    const params = {};

    this.consolGroupEditService.getTaxOwners(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading tax owners', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.taxOwnersList = data.jsonObject || [];
          console.log('[ConsolGroupEdit] Tax owners loaded:', this.taxOwnersList.length, 'items');
          this.selectedTaxOwner = this.taxOwnersList.length > 0 ? this.taxOwnersList[0] : null;

          if ((this.colDataMap === 'GROUP_EDIT' || this.colDataMap === 'US_TAX_OWNER') && this.rowData) {
            console.log('[ConsolGroupEdit] Looking for tax owner with SSO_ID:', this.rowData.GROUP_OWNER);
            const taxOwnerInd = this.taxOwnersList.findIndex(
              (item: any) => item.SSO_ID === this.rowData.GROUP_OWNER
            );
            console.log('[ConsolGroupEdit] Tax owner index found:', taxOwnerInd);
            this.selectedTaxOwner = taxOwnerInd >= 0 ? this.taxOwnersList[taxOwnerInd] : null;
            console.log('[ConsolGroupEdit] selectedTaxOwner set to:', this.selectedTaxOwner?.FULL_NAME, 'SSO_ID:', this.selectedTaxOwner?.SSO_ID);
          }
        }
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading tax owners:', error);
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadCountryList(): void {
    const params = {
      tax_year: this.filter_tax_year
    };

    this.consolGroupEditService.getCountryList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading country list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.countryList = data.jsonObject || [];

          if (this.rowData && this.rowData.COUNTRY) {
            const countryInd = this.countryList.findIndex(
              (item: any) => item.VALUE === this.rowData.COUNTRY
            );
            this.selectedGroupCountry = countryInd >= 0 ? this.countryList[countryInd] : null;

            if (this.selectedGroupCountry) {
              this.loadNonMembersList(this.selectedGroupCountry.VALUE);
            }
          }
        }
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading country list:', error);
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadNonMembersList(group_country: string): void {
    this.nonMemerLoading = true;
    this.cdr.detectChanges();

    const params = {
      tax_year: this.filter_tax_year,
      group_id: this.rowData?.GROUP_ID || 0,
      country: group_country
    };

    this.consolGroupEditService.getNonMembersList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        this.nonMemerLoading = false;
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading non-members list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.nonMembersList = data.jsonObject || [];
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.nonMemerLoading = false;
        console.error('Error loading non-members list:', error);
        this.cdr.detectChanges();
      }
    );
  }

  loadMembersList(): void {
    const params = {
      tax_year: this.filter_tax_year,
      group_id: this.rowData?.GROUP_ID || 0
    };

    this.consolGroupEditService.getMembersList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading members list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.membersList = data.jsonObject || [];
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading members list:', error);
        this.cdr.detectChanges();
      }
    );
  }

  loadMembersAmtList(): void {
    const params = {
      tax_year: this.filter_tax_year,
      group_id: this.rowData?.GROUP_ID || 0
    };

    this.consolGroupEditService.getMembersConsolAmts(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (data.callSuccess !== '1') {
          this._snackBar.open(data.errorMessage || 'Error loading members amounts', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.membersAmountList = data.jsonObject || [];

          if (this.membersAmountList.length > 0 && this.membersAmountList[0].DATEFILED) {
            this.dateFiled = this.parseLocalDate(this.membersAmountList[0].DATEFILED);
          }

          this.credit_us_amt = {};
          this.non_credit_us_amt = {};

          this.membersAmountList.forEach((item: any) => {
            this.credit_us_amt[item.COMBINATION_KEY] = item.CREDIT_AMT || 0;
            this.non_credit_us_amt[item.COMBINATION_KEY] = item.NON_CREDIT_AMT || 0;
          });

          this.setTotal();
        }
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading members amounts:', error);
        this.showFormLoader = false;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  changeFiscalStart(): void {
    if (this.selectedFiscalStart) {
      this.loadGoldHeadLeid(parseInt(this.selectedFiscalStart.TAX_YEAR));
    }
  }

  changeFiscalEnd(): void {
    if (this.selectedFiscalEnd && this.selectedFiscalStart) {
      if (this.selectedFiscalEnd.TAX_YEAR < this.selectedFiscalStart.TAX_YEAR) {
        this._snackBar.open('Fiscal Year End cannot be less than fiscal start year.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    }

    if (!this.isSaveClicked) {
      this.selectedTaxOwnerEnd = this.selectedFiscalEnd;
    }
  }

  changeCountry(): void {
    if (this.selectedGroupCountry) {
      this.loadNonMembersList(this.selectedGroupCountry.VALUE);
    }
  }

  setTotal(): void {
    let total_credit_amt = 0;
    let total_non_credit_amt = 0;

    Object.values(this.credit_us_amt).forEach(value => {
      total_credit_amt += value || 0;
    });
    this.credit_us_amt_total = total_credit_amt;

    Object.values(this.non_credit_us_amt).forEach(value => {
      total_non_credit_amt += value || 0;
    });
    this.non_credit_us_amt_total = total_non_credit_amt;

    this.cdr.detectChanges();
  }

  addMembers(item: any): void {
    const addMemberInd = this.nonMembersList.findIndex(
      (member: any) => member.COMBINATION_KEY === item.COMBINATION_KEY
    );

    if (addMemberInd >= 0) {
      this.nonMembersList.splice(addMemberInd, 1);
      this.membersList.push(item);
    }

    // Remove from delete list if item is being re-added
    const removeMemberInd = this.memberDeleteList.findIndex(
      (member: any) => member.COMBINATION_KEY === item.COMBINATION_KEY
    );
    if (removeMemberInd >= 0) {
      this.memberDeleteList.splice(removeMemberInd, 1);
    }

    this.cdr.detectChanges();
  }

  removeMembers(item: any): void {
    const removeMemberInd = this.membersList.findIndex(
      (member: any) => member.COMBINATION_KEY === item.COMBINATION_KEY
    );

    // Check if this is the head of the group
    if (item.COMBINATION_KEY === this.rowData?.GROUP_LEID_CC_KEY) {
      this._snackBar.open(`You cannot delete ${item.LEID} as it is head of the group`, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (removeMemberInd >= 0) {
      // Add to delete list if it's an existing record
      if ('RECORD_TYPE' in item) {
        this.memberDeleteList.push(item);
      }
      this.membersList.splice(removeMemberInd, 1);
      this.nonMembersList.push(item);
    }

    this.cdr.detectChanges();
  }

  openCalendar(): void {
    this.calendar.opened = true;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    switch (this.colDataMap) {
      case 'GROUP_EDIT':
      case 'ADD_GROUP':
        this.isSaveClicked = true;
        this.changeFiscalEnd();

        // Validate form
        if (!this.validateGroupForm()) {
          this._snackBar.open('Please correct the errors below', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          return;
        }
        this.saveGroup();
        break;

      case 'MEMBERS_EDIT':
        this.saveUpdateMembers();
        break;

      case 'ALLOCATE_LIABILITY':
        this.saveUpdateAllocations();
        break;

      case 'US_TAX_OWNER':
        this.saveUpdateTaxOwners();
        break;
    }
  }

  validateGroupForm(): boolean {
    if (!this.selectedFiscalStart) return false;
    if (!this.selectedFiscalEnd) return false;
    if (!this.GROUP_NAME || this.GROUP_NAME.trim() === '') return false;
    if (!this.selectedCurrency) return false;
    if (!this.selectedTaxOwner) return false;
    if (this.colDataMap !== 'GROUP_EDIT' && !this.selectedHeadLeid) return false;

    // Validate fiscal end >= fiscal start
    if (this.selectedFiscalEnd && this.selectedFiscalStart) {
      if (this.selectedFiscalEnd.TAX_YEAR < this.selectedFiscalStart.TAX_YEAR) {
        return false;
      }
    }

    return true;
  }

  saveGroup(): void {
    this.crudLoading = true;
    this.cdr.detectChanges();

    const reqParams = {
      tax_year: this.filter_tax_year,
      scenario: this.raf_scenario,
      jcd_key: 250
    };

    const params = [{
      group_id: this.GROUP_ID,
      fiscal_start: this.selectedFiscalStart?.TAX_YEAR,
      fiscal_end: this.selectedFiscalEnd?.TAX_YEAR,
      group_leid_cc_key: (this.colDataMap === 'GROUP_EDIT')
        ? this.rowData?.GROUP_LEID_CC_KEY
        : this.selectedHeadLeid?.COMBINATION_KEY,
      group_name: this.GROUP_NAME,
      group_type: this.GROUP_TYPE,
      currency: this.selectedCurrency?.CURR_CODE,
      tax_owner: this.selectedTaxOwner?.SSO_ID,
      scenario: this.raf_scenario
    }];

    this.consolGroupEditService.saveUpdateGroups(
      this.GENERAL_CONFIG.base_url,
      params,
      reqParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.crudLoading = false;
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        } else {
          // For GROUP_EDIT mode, also save tax owner separately since the main save doesn't update it
          if (this.colDataMap === 'GROUP_EDIT' && this.selectedTaxOwner) {
            console.log('[ConsolGroupEdit] Group saved, now saving tax owner separately...');
            this.saveGroupTaxOwner();
          } else {
            this.crudLoading = false;
            console.log('[ConsolGroupEdit] Group saved successfully. Updating rowData.GROUP_OWNER from', this.rowData?.GROUP_OWNER, 'to', this.selectedTaxOwner?.SSO_ID);
            if (this.rowData) {
              this.rowData.GROUP_OWNER = this.selectedTaxOwner?.SSO_ID;
            }
            this._snackBar.open('New Group has been added successfully', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.requestGridUpdate.emit('refresh');
            this.cdr.detectChanges();
          }
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  // Save tax owner separately after group save in GROUP_EDIT mode
  private saveGroupTaxOwner(): void {
    const reqParams = {
      tax_year: this.filter_tax_year,
      scenario: this.raf_scenario,
      jcd_key: 250
    };

    const paramsArray = [{
      tax_year: this.filter_tax_year,
      group_id: this.GROUP_ID,
      tax_owner: this.selectedTaxOwner?.SSO_ID
    }];

    console.log('[ConsolGroupEdit] saveGroupTaxOwner called with:', {
      paramsArray,
      reqParams,
      GROUP_ID: this.GROUP_ID,
      'rowData.GROUP_ID': this.rowData?.GROUP_ID,
      selectedTaxOwner: this.selectedTaxOwner
    });

    this.consolGroupEditService.saveUpdateTaxOwners(
      this.GENERAL_CONFIG.base_url,
      paramsArray,
      reqParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          console.log('[ConsolGroupEdit] Tax Owner saved successfully. Updating rowData.GROUP_OWNER from', this.rowData?.GROUP_OWNER, 'to', this.selectedTaxOwner?.SSO_ID);
          // Set flag to prevent _rowData setter from reverting selectedTaxOwner
          this.justSavedTaxOwner = true;
          if (this.rowData) {
            this.rowData.GROUP_OWNER = this.selectedTaxOwner?.SSO_ID;
          }
          this._snackBar.open('Group has been updated successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving tax owner. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  saveUpdateMembers(): void {
    this.crudLoading = true;
    this.cdr.detectChanges();

    const reqParams = {
      tax_year: this.filter_tax_year,
      scenario: this.raf_scenario,
      jcd_key: 250
    };

    const paramsArray: any[] = [];

    // Add all current members
    this.membersList.forEach((item: any) => {
      paramsArray.push({
        group_id: this.GROUP_ID,
        tax_year: this.filter_tax_year,
        combination_key: item.COMBINATION_KEY,
        record_type: 'A',
        fiscal_start: this.rowData?.FISCAL_START,
        fiscal_end: this.rowData?.FISCAL_END
      });
    });

    // Add deleted members
    this.memberDeleteList.forEach((item: any) => {
      paramsArray.push({
        group_id: this.GROUP_ID,
        tax_year: this.filter_tax_year,
        combination_key: item.COMBINATION_KEY,
        record_type: 'D'
      });
    });

    this.consolGroupEditService.saveUpdateConsolMembers(
      this.GENERAL_CONFIG.base_url,
      paramsArray,
      reqParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('New Members have been added successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  saveUpdateAllocations(): void {
    this.crudLoading = true;
    this.cdr.detectChanges();

    const reqParams = {
      tax_year: this.filter_tax_year,
      scenario: this.raf_scenario,
      jcd_key: 250
    };

    const paramsArray: any[] = [];
    const dateFiledStr = this.dateFiled ? this.formatDate(this.dateFiled) : '';

    // Add credit amounts
    Object.keys(this.credit_us_amt).forEach(key => {
      paramsArray.push({
        group_id: this.GROUP_ID,
        group_currency: this.GROUP_CURRENCY,
        tax_year: this.filter_tax_year,
        combination_key: key,
        amt: this.credit_us_amt[key],
        trans_type: 'CA',
        scenario: this.raf_scenario,
        dateFiled: dateFiledStr
      });
    });

    // Add non-credit amounts
    Object.keys(this.non_credit_us_amt).forEach(key => {
      paramsArray.push({
        group_id: this.GROUP_ID,
        group_currency: this.GROUP_CURRENCY,
        tax_year: this.filter_tax_year,
        combination_key: key,
        amt: this.non_credit_us_amt[key],
        trans_type: 'NCA',
        scenario: this.raf_scenario,
        dateFiled: dateFiledStr
      });
    });

    this.consolGroupEditService.saveUpdateConsolGroupAmts(
      this.GENERAL_CONFIG.base_url,
      paramsArray,
      reqParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Amounts have been added successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  saveUpdateTaxOwners(): void {
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Validate
    if (!this.selectedTaxOwner) {
      this._snackBar.open('US Tax Owner is required.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const reqParams = {
      tax_year: this.filter_tax_year,
      scenario: this.raf_scenario,
      jcd_key: 250
    };

    const paramsArray = [{
      tax_year: this.filter_tax_year,
      group_id: this.GROUP_ID,
      tax_owner: this.selectedTaxOwner?.SSO_ID
    }];
    
    console.log('[ConsolGroupEdit] saveTaxOwner params:', {
      paramsArray,
      GROUP_ID: this.GROUP_ID,
      'rowData.GROUP_ID': this.rowData?.GROUP_ID,
      reqParams
    });

    this.consolGroupEditService.saveUpdateTaxOwners(
      this.GENERAL_CONFIG.base_url,
      paramsArray,
      reqParams,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this._snackBar.open(result.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          // Update rowData with new tax owner to prevent reverting to old value
          console.log('[ConsolGroupEdit] Tax Owner saved successfully. Updating rowData.GROUP_OWNER from', this.rowData?.GROUP_OWNER, 'to', this.selectedTaxOwner?.SSO_ID);
          // Set flag to prevent _rowData setter from reverting selectedTaxOwner
          this.justSavedTaxOwner = true;
          if (this.rowData) {
            this.rowData.GROUP_OWNER = this.selectedTaxOwner?.SSO_ID;
          }
          this._snackBar.open('Tax Owner has been updated successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.isInitialized = false;
    this.isLoading = false;
    this.memberDeleteList = [];
    this.init();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private formatDate(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Parse a date string as local midnight to avoid UTC timezone shift.
   * new Date("YYYY-MM-DD") parses as UTC midnight, which in timezones behind UTC (e.g. EST)
   * causes getDate() to return the previous day.
   */
  private parseLocalDate(dateStr: string): Date {
    if (!dateStr) return new Date();
    const parts = dateStr.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (parts) {
      return new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
    }
    const slashParts = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashParts) {
      return new Date(parseInt(slashParts[3]), parseInt(slashParts[1]) - 1, parseInt(slashParts[2]));
    }
    const d = new Date(dateStr);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  onDateFiledChange(value: string): void {
    this.dateFiled = value ? this.parseLocalDate(value) : null;
    this.cdr.detectChanges();
  }

  get dateFiledString(): string {
    if (!this.dateFiled) return '';
    const year = this.dateFiled.getFullYear();
    const month = (this.dateFiled.getMonth() + 1).toString().padStart(2, '0');
    const day = this.dateFiled.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper for comparing dropdown values
  compareDropdown(item1: any, item2: any): boolean {
    if (!item1 || !item2) return false;
    return item1 === item2 || (item1.TAX_YEAR && item2.TAX_YEAR && item1.TAX_YEAR === item2.TAX_YEAR);
  }

  // Helper for comparing tax owner objects
  compareTaxOwner(item1: any, item2: any): boolean {
    if (!item1 || !item2) return false;
    return item1 === item2 || (item1.SSO_ID && item2.SSO_ID && item1.SSO_ID === item2.SSO_ID);
  }
}
